import React, { Dispatch, SyntheticEvent, useState } from "react";
import { toast } from "../../common/notify";
import { CallCheckMeetingStatus, setMeetingItem } from "../../api/MeetingApi";
import { MeetingStatuses } from "../../data/MeetingStatus";
import { SLinkActions, updateMeetingStatusAction } from "../../state/actions";
import { SLinkState, UserRoles } from "../../state/state";

export interface MeetingItemNavigationProps {
  onNext: () => void;
  onPrevious: () => void;
  loading: boolean;
  state: SLinkState;
  dispatch: Dispatch<SLinkActions>;
}

function meetingStatusChipClass(status: string | undefined): string {
  switch (status) {
    case MeetingStatuses.InSession:
      return "sl-chip-status-new";
    case MeetingStatuses.Voting:
      return "sl-chip-status-voting";
    case MeetingStatuses.Completed:
      return "sl-chip-status-voted";
    default:
      return "sl-chip-type";
  }
}

function MeetingItemNavigation({ onNext, onPrevious, loading, state, dispatch }: MeetingItemNavigationProps) {
  const [refreshDisabled, setRefreshDisabled] = useState(false);
  const [callingRefresh, setCallingRefresh] = useState(false);
  const [callingClearItem, setCallingClearItem] = useState(false);

  const voting = state.meetingStatus?.status === MeetingStatuses.Voting;

  function previous(event: SyntheticEvent) {
    event.preventDefault();
    onPrevious();
  }

  function next(event: SyntheticEvent) {
    event.preventDefault();
    onNext();
  }

  function refresh(event: SyntheticEvent) {
    setRefreshDisabled(true);
    setCallingRefresh(true);
    event.preventDefault();
    CallCheckMeetingStatus()
      .then((status) => {
        let isNewVersion = state.meetingStatus ? state.meetingStatus.version < status.version : true;
        dispatch(updateMeetingStatusAction(status));
        toast.success(`Meeting status refreshed (v${status.version} - ${status.lastUpdate} new ${isNewVersion})`);
      })
      .catch((error: Error) => {
        toast.error(`Error on refreshing state '${error.message}'`);
      })
      .finally(() => {
        setCallingRefresh(false);
        setTimeout(() => {
          setRefreshDisabled(false);
        }, 10000);
      });
  }
  function clear(event: SyntheticEvent) {
    setCallingClearItem(true);
    event.preventDefault();
    setMeetingItem("NONE")
      .then((status) => {
        dispatch(updateMeetingStatusAction(status));
        toast.success("Item of business cleared");
      })
      .catch((error: Error) => {
        toast.error(`Error on clearing item '${error.message}'`);
      })
      .finally(() => {
        setCallingClearItem(false);
      });
  }

  return (
    <div className="sl-toolbar mb-3">
      <div className="d-flex flex-wrap align-items-center gap-2">
        <button className="btn btn-light" onClick={previous} disabled={voting}>
          ← Previous
        </button>
        <button className="btn btn-primary" onClick={next} disabled={voting}>
          Next Item →
        </button>
        {state.user?.userRoles.includes(UserRoles.Admin) ? (
          <button className="btn btn-light" onClick={clear} disabled={callingClearItem || voting}>
            Clear Item
          </button>
        ) : (
          <></>
        )}
        <span className={`sl-chip ${meetingStatusChipClass(state.meetingStatus?.status)}`}>Meeting {state.meetingStatus?.status}</span>
        {loading ? (
          <span className="d-inline-flex align-items-center gap-2 text-muted" role="status">
            <span className="spinner-border spinner-border-sm text-primary" aria-hidden="true"></span>
            Loading...
          </span>
        ) : (
          <></>
        )}
        <div className="ms-auto d-flex flex-wrap align-items-center gap-3">
          <span className="text-muted" title="Connected users by house">
            <strong className="sl-num">{state.statUserCount?.totalUsers ?? 0}</strong> online · Lay <span className="sl-num">{state.statUserCount?.usersLay ?? 0}</span> · Clergy{" "}
            <span className="sl-num">{state.statUserCount?.usersClergy ?? 0}</span> · Bishops <span className="sl-num">{state.statUserCount?.usersBishops ?? 0}</span>
          </span>
          <button className="btn btn-light" onClick={refresh} disabled={callingRefresh || refreshDisabled}>
            Refresh
          </button>
        </div>
      </div>
    </div>
  );
}

export default MeetingItemNavigation;
